import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Package, Pencil, Trash2, AlertTriangle, PlayCircle } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";

const LOW_STOCK = 5;

const stockBadge = (stock) => {
  if (stock <= 0) return <Badge className="bg-red-500">Out of stock</Badge>;
  if (stock <= LOW_STOCK) return <Badge className="bg-amber-500"><AlertTriangle className="w-3 h-3 mr-1" />Low ({stock})</Badge>;
  return <Badge className="bg-green-600">{stock} in stock</Badge>;
};

const statusBadge = (status) => (
  <Badge className={status === "approved" ? "bg-green-600" : status === "pending" ? "bg-amber-500" : "bg-red-500"}>{status}</Badge>
);

export const ProductsManager = ({ products = [], format, search = "", onCreate, onEdit, onDelete }) => {
  const filtered = products.filter((p) => !search || p.title.toLowerCase().includes(search.toLowerCase()));
  const lowCount = products.filter((p) => p.stock > 0 && p.stock <= LOW_STOCK).length;
  const outCount = products.filter((p) => p.stock <= 0).length;

  const exportCols = [
    { header: "Title", accessor: (p) => p.title },
    { header: "SKU", accessor: (p) => p.sku || "" },
    { header: "Category", accessor: (p) => p.category || "" },
    { header: "Type", accessor: (p) => p.product_type || (p.variants?.length ? "variable" : "simple") },
    { header: "Price (EUR)", accessor: (p) => (p.price ?? 0).toFixed(2) },
    { header: "Stock", accessor: (p) => p.stock ?? 0 },
    { header: "Sold", accessor: (p) => p.sold || 0 },
    { header: "Status", accessor: (p) => p.status },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <ExportMenu filename="products" title="Products" columns={exportCols} rows={filtered} testid="products-export" />
      </div>
      {(lowCount > 0 || outCount > 0) && (
        <Card className="p-4 flex items-center gap-3 border-amber-300 bg-amber-50 dark:bg-amber-950/20" data-testid="stock-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm">
            {outCount > 0 && <span className="font-semibold">{outCount} out of stock</span>}
            {outCount > 0 && lowCount > 0 && " · "}
            {lowCount > 0 && <span className="font-semibold">{lowCount} running low</span>}
            <span className="text-muted-foreground"> — restock to keep selling.</span>
          </p>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Inventory</TableHead>
              <TableHead className="hidden sm:table-cell">Sold</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="relative w-11 h-11 rounded-lg overflow-hidden bg-muted shrink-0">
                      {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-muted-foreground m-3" />}
                      {p.videos?.length > 0 && <PlayCircle className="w-4 h-4 text-white absolute bottom-0.5 right-0.5 drop-shadow" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate max-w-[220px]">{p.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{p.category?.replace("-", " ")}{p.variants?.length ? ` · ${p.variants.length} variants` : ""}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm font-semibold">{format(p.price)}{p.discount_price ? <span className="block text-xs text-muted-foreground line-through">{format(p.price)}</span> : null}</TableCell>
                <TableCell>{stockBadge(p.stock)}</TableCell>
                <TableCell className="hidden sm:table-cell text-sm">{p.sold || 0}</TableCell>
                <TableCell>{statusBadge(p.status)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`edit-prod-${p.product_id}`} onClick={() => onEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`del-prod-${p.product_id}`} onClick={() => onDelete(p.product_id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {products.length === 0 ? "No products yet." : "No products match your search."}
                {products.length === 0 && <div className="mt-3"><Button size="sm" className="rounded-full" onClick={onCreate}>Add your first product</Button></div>}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
